/**
 * Variable Utilities
 * 
 * Centralized utility functions for variable conversion and manipulation.
 * These are pure functions with no React dependencies for maximum reusability.
 * 
 * Variable Format:
 * - Stored format: ${_id:variableId} (used internally for persistence)
 * - Display format: ${ENV.variableName} or ${PROJECT.variableName} (shown to users)
 */

/**
 * Converts variable IDs to display format
 * ${_id:xxx} -> ${ENV.var} or ${PROJECT.var}
 * 
 * @param {string} storedValue - Value containing ${_id:xxx} patterns
 * @param {Array} variablesList - Array of variable objects with {id, key}
 * @param {Object} variablesListDescriptions - Object mapping variable IDs to descriptions
 * @returns {string} - Value with display format variables
 */
/** Strip ${_id:uuid} / _id:uuid / bare uuid wrappers from agent keys. */
export const normalizeVariableId = (raw) => {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    const wrapped = s.match(/^\$\{(?:_id|id|ID):([^}]+)\}$/);
    if (wrapped) return wrapped[1].trim();
    const prefixed = s.match(/^(?:_id|id|ID):(.+)$/i);
    if (prefixed) return prefixed[1].trim();
    return s;
};

const isUuidLike = (s) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(s || '').trim());

function getDescriptionForVariableId(variablesListDescriptions, idRaw) {
    if (!variablesListDescriptions || idRaw == null) return null;
    const id = normalizeVariableId(idRaw);
    if (!id) return null;
    if (variablesListDescriptions[id]) return variablesListDescriptions[id];
    const il = id.toLowerCase();
    for (const k of Object.keys(variablesListDescriptions)) {
        if (normalizeVariableId(k).toLowerCase() === il) return variablesListDescriptions[k];
    }
    return null;
}

function findVariableById(variablesList, idRaw) {
    if (!variablesList?.length || idRaw == null) return undefined;
    const id = normalizeVariableId(idRaw);
    if (!id) return undefined;
    const il = id.toLowerCase();
    return variablesList.find(
        (v) =>
            (v?.id != null && String(v.id).trim().toLowerCase() === il) ||
            (v?.variable_id != null && String(v.variable_id).trim().toLowerCase() === il)
    );
}

export const convertIdsToDisplay = (storedValue, variablesList = [], variablesListDescriptions = {}) => {
    if (!storedValue || typeof storedValue !== 'string') return storedValue;
    
    // ${_id:xxx}, ${id:xxx}, ${ID:xxx} (AI / legacy payloads may omit the underscore)
    return storedValue.replace(/\$\{(?:_id|id|ID):([^}]+)\}/g, (match, id) => {
        const description = getDescriptionForVariableId(variablesListDescriptions, id);
        const variable = findVariableById(variablesList, id);

        if (description) {
            const prefix = description.systemVariable ? 'PROJECT.' : 'ENV.';
            const key = variable?.key || description.key || id;
            return `\${${prefix}${key}}`;
        }
        // List may still include the id/key when description map is empty or out of sync
        if (variable?.key) {
            return `\${ENV.${variable.key}}`;
        }
        return match;
    });
};

/**
 * Converts display format to stored IDs
 * ${ENV.var} or ${PROJECT.var} -> ${_id:id}
 * 
 * @param {string} displayValue - Value containing ${ENV.xxx} or ${PROJECT.xxx} patterns
 * @param {Array} variablesList - Array of variable objects with {id, key}
 * @param {Object} variablesListDescriptions - Object mapping variable IDs to descriptions
 * @returns {string} - Value with stored format variables (${_id:xxx})
 */
export const convertDisplayToIds = (displayValue, variablesList = [], variablesListDescriptions = {}) => {
    if (!displayValue || typeof displayValue !== 'string') return displayValue;
    
    // Normalize keys so "bing url" matches stored key "bing_url" (spaces vs underscores).
    const normKey = (k) => String(k).trim().replace(/\s+/g, '_');

    // Find all ${ENV.xxx} or ${PROJECT.xxx} patterns and replace with ID format
    return displayValue.replace(/\$\{(ENV|PROJECT)\.([^}]+)\}/g, (match, prefix, keyPart) => {
        const nk = normKey(keyPart);
        // Find the variable by key and prefix type
        const variable = variablesList.find(s => {
            const desc = variablesListDescriptions[s.id];
            if (!desc) return false;
            const isProject = desc.systemVariable;
            const matchesPrefix = (prefix === 'PROJECT' && isProject) || (prefix === 'ENV' && !isProject);
            if (!matchesPrefix) return false;
            const candidates = [s.key, desc.key].filter(Boolean).map((k) => String(k));
            return candidates.some(
                (k) => k === keyPart || normKey(k) === nk
            );
        });
        
        if (variable) {
            return `\${_id:${variable.id}}`;
        }
        // If not found, keep original (might be manually typed)
        return match;
    });
};

/**
 * Migrates old variable formats to the new ${_id:id} format
 * Handles backward compatibility for:
 * - ${ENV.var} or ${PROJECT.var} -> ${_id:id}
 * - ${id} (old format without _id: prefix) -> ${_id:id}
 * 
 * @param {string} value - Value that may contain old format variables
 * @param {Array} variablesList - Array of variable objects
 * @param {Object} variablesListDescriptions - Object mapping variable IDs to descriptions
 * @returns {string} - Value with migrated variable format
 */
export const migrateOldVariableFormat = (value, variablesList = [], variablesListDescriptions = {}) => {
    if (!value || typeof value !== 'string') return value;
    
    // Check if value contains old format patterns
    const hasOldFormat = value.includes('${') && 
        (value.includes('${ENV.') || value.includes('${PROJECT.') || 
         (value.match(/\$\{([^}]+)\}/g) && !value.includes('${_id:')));
    
    if (!hasOldFormat) return value;
    
    // First convert ${ENV.xxx} and ${PROJECT.xxx} to ${_id:id}
    let convertedValue = convertDisplayToIds(value, variablesList, variablesListDescriptions);
    
    // Then handle old ${id} format (convert to ${_id:id})
    convertedValue = convertedValue.replace(/\$\{([^}]+)\}/g, (match, content) => {
        // Skip if it's already ${_id:} or ${ENV.} or ${PROJECT.} format
        if (content.startsWith('_id:') || content.startsWith('ENV.') || content.startsWith('PROJECT.')) {
            return match;
        }
        // Check if content is a valid ID
        const description = variablesListDescriptions[content];
        if (description) {
            return `\${_id:${content}}`;
        }
        return match;
    });
    
    return convertedValue;
};

/**
 * Checks if a value contains variable patterns
 * 
 * @param {string} value - Value to check
 * @returns {boolean} - True if value contains variable patterns
 */
export const containsVariables = (value) => {
    if (!value || typeof value !== 'string') return false;
    return /\$\{(?:(?:_id|id|ID):|(?:ENV|PROJECT)\.)[^}]+\}/.test(value);
};

/**
 * Extracts all variable IDs from a value
 * 
 * @param {string} value - Value containing variable patterns
 * @returns {Array<string>} - Array of variable IDs found in the value
 */
export const extractVariableIds = (value) => {
    if (!value || typeof value !== 'string') return [];
    
    const ids = [];
    const matches = value.matchAll(/\$\{(?:_id|id|ID):([^}]+)\}/g);
    for (const match of matches) {
        ids.push(match[1]);
    }
    return ids;
};

/** Mask shown in Mind / vars panel — must match agent scrub output. */
export const SECRET_MASK_DISPLAY = '**************';

/**
 * Map agent variablesUsed row → Vars panel label (ENV.key / PROJECT.key, never _id:uuid).
 */
export const resolveVariablesUsedRow = (
    row = {},
    variablesList = [],
    variablesListDescriptions = {}
) => {
    const id = normalizeVariableId(
        row.id || row.variableId || row.variable_id || row.key || row.name || ''
    );
    const keyHint = String(row.key || row.name || '').trim();
    const fromList = id ? findVariableById(variablesList, id) : null;
    const fromDesc = id ? getDescriptionForVariableId(variablesListDescriptions, id) : null;
    const humanKey =
        fromList?.key ||
        fromList?.name ||
        fromDesc?.key ||
        (keyHint &&
        !isUuidLike(normalizeVariableId(keyHint)) &&
        !/^(?:_id|id|ID):/i.test(keyHint) &&
        !/^\$\{(?:_id|id|ID):/i.test(keyHint)
            ? keyHint
            : null);
    const prefix = fromDesc?.systemVariable ? 'PROJECT.' : 'ENV.';
    const name = humanKey ? `${prefix}${humanKey}` : id || keyHint || 'variable';
    const isSecret = Boolean(row.isSecret ?? fromDesc?.isSecret ?? fromList?.isSecret);
    return {
        key: id || name,
        name,
        isSecret,
        value: isSecret ? SECRET_MASK_DISPLAY : row.value,
    };
};

const SCRUB_MIN_LEN = 8;
const SCRUB_DENY = new Set([
    'password', 'passwd', 'secret', 'token', 'test', 'admin',
    '123456', 'qwerty', 'changeme', 'default', 'null', 'undefined', 'none', 'user', 'guest',
]);

/**
 * Collect secret plaintexts for FE defense-in-depth scrubbing (Mind timeline only).
 * security: secrets — values stay local; never sent on WS start payload.
 */
export const collectSecretPlaintexts = (
    variablesList = [],
    environmentId = '',
    variablesListDescriptions = {}
) => {
    const values = [];
    const seen = new Set();
    for (const variable of variablesList || []) {
        const id = String(variable?.id || variable?.variable_id || '');
        const desc = getDescriptionForVariableId(variablesListDescriptions, id);
        const isSecret = Boolean(desc?.isSecret || variable?.isSecret);
        if (!isSecret) continue;
        let value = variable?.value;
        if (environmentId && Array.isArray(variable?.environment_values)) {
            const match = variable.environment_values.find(
                (ev) => String(ev.environment_id || ev.environmentId) === String(environmentId)
            );
            if (match?.value != null) value = match.value;
        }
        if (typeof value !== 'string' || value.length < SCRUB_MIN_LEN) continue;
        if (SCRUB_DENY.has(value.toLowerCase())) continue;
        if (seen.has(value)) continue;
        seen.add(value);
        values.push(value);
    }
    values.sort((a, b) => b.length - a.length);
    return values;
};

/** Replace known secret plaintexts in agent egress strings. */
export const scrubSecretPlaintexts = (text, secretValues = []) => {
    if (!text || !secretValues?.length) return text;
    let result = String(text);
    for (const secret of secretValues) {
        const escaped = secret.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = new RegExp(
            `(?<![\\w@.-])${escaped}(?![\\w@.-])`,
            /^[a-z0-9]+$/i.test(secret) ? 'gi' : 'g'
        );
        result = result.replace(pattern, SECRET_MASK_DISPLAY);
    }
    return result;
};

export const instructionUsesSecretVariable = (
    text,
    variablesList = [],
    variablesListDescriptions = {}
) =>
    extractVariableIds(text || '').some((id) => {
        const desc = getDescriptionForVariableId(variablesListDescriptions, id);
        const variable = findVariableById(variablesList, id);
        return Boolean(desc?.isSecret || variable?.isSecret);
    });

/**
 * Build agent-safe variable payload (STRICT mode: no values).
 *
 * For strict "no secrets in websocket payloads" we only send:
 * - variableMetadata: { [uuid]: { key, isSecret } }
 * - variableMap is intentionally empty (agent must fetch values server-side)
 */
export const buildVariablePayloadForAgentStrict = (
    instructions = [],
    variablesList = [],
    variablesListDescriptions = {}
) => {
    const referenced = new Set();
    for (const inst of instructions || []) {
        const text = typeof inst === 'string' ? inst : (inst?.text || '');
        extractVariableIds(text).forEach((id) => referenced.add(String(id).trim()));
    }

    const variableMetadata = {};
    for (const idRaw of referenced) {
        const id = String(idRaw).trim();
        const desc = getDescriptionForVariableId(variablesListDescriptions, id);
        const variable = findVariableById(variablesList, id);
        if (!desc && !variable) continue;
        const key = variable?.key || desc?.key || id;
        variableMetadata[id] = {
            key,
            isSecret: Boolean(desc?.isSecret || variable?.isSecret),
            // ponytail: name kept for agent tests / legacy readers
            name: key,
            type: desc?.systemVariable ? 'system' : 'env',
        };
    }

    return { variableMap: {}, variableMetadata };
};

/**
 * Replaces file refs @[id] with file names for display.
 * Normalizes id (trim + lowercase) so UUIDs match regardless of case/whitespace.
 *
 * @param {string} value - Text that may contain @[id] patterns
 * @param {Array<{id: string|number, name: string}>} fileSuggestions - List of files with id and name
 * @returns {string} - Text with @[id] replaced by file name, or original if no match
 */
export const replaceFileRefsWithNames = (value, fileSuggestions = []) => {
    if (value == null || typeof value !== 'string') return value;
    const norm = (x) => String(x).trim().toLowerCase();
    const byId = new Map((fileSuggestions || []).map((f) => [norm(f.id), f.name]));
    return value.replace(/@\[([^\]]+)\]/g, (_, id) => byId.get(norm(id)) ?? `@[${id}]`);
};

/**
 * Resolve stored variable ids and file refs for read-only display (matches Automation Studio left panel).
 */
export const formatAutomationTextForDisplay = (
    value,
    variablesList = [],
    variablesListDescriptions = {},
    fileSuggestions = []
) => {
    if (value == null || typeof value !== 'string') return value;
    const withVars = convertIdsToDisplay(value, variablesList, variablesListDescriptions);
    return replaceFileRefsWithNames(withVars, fileSuggestions);
};

/**
 * Union file suggestion lists (e.g. automation studio + replay) by id; primary wins on duplicate ids.
 */
export const mergeFileSuggestionLists = (primary = [], secondary = []) => {
    const norm = (x) => String(x).trim().toLowerCase();
    const map = new Map();
    for (const f of secondary) {
        if (f?.id != null && !map.has(norm(f.id))) map.set(norm(f.id), f);
    }
    for (const f of primary) {
        if (f?.id != null) map.set(norm(f.id), f);
    }
    return Array.from(map.values());
};

